// File: DashboardLayout.tsx
// Dashboard-only layout primitives. ContentCard is NOT used here on purpose:
// these cards always fill their grid cell, so every card in a row has the same height.
import React from 'react';
import { Box, Card, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ErrorBoundary } from 'src/core/ui/helpers/ErrorBoundry';

export type DashboardArea = 'chart' | 'performance' | 'channel' | 'operations' | 'kpi';

/*
 * Layout is driven by the dashboard's OWN width (CSS container queries), not the window,
 * so it also reflows correctly when the side menu opens/closes.
 *
 *   < 900px   : one column
 *   900-1299  : chart / performance (full width), channel + operations, kpi (full width)
 *   >= 1300   : chart (2/3) + performance (1/3), then channel / operations / kpi (1/3 each)
 */
const gridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"chart" "performance" "channel" "operations" "kpi"',
  '@container dashboard (min-width: 900px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateAreas: '"chart chart" "performance performance" "channel operations" "kpi kpi"',
  },
  '@container dashboard (min-width: 1300px)': {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gridTemplateAreas: '"chart chart performance" "channel operations kpi"',
  },
} as SxProps<Theme>;

export const DashboardGrid = ({ children }: { children?: React.ReactNode }) => (
  <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, containerType: 'inline-size', containerName: 'dashboard' } as SxProps<Theme>}>
    <Box sx={gridSx}>{children}</Box>
  </Box>
);

export type DashboardCardProps = {
  area: DashboardArea;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  tooltip?: string;
  /** Rendered in the top-right corner, on the same row as the title. */
  action?: React.ReactNode;
  /** Rendered between the header and the body (e.g. chart series picker). */
  toolbar?: React.ReactNode;
  bodySx?: SxProps<Theme>;
  children?: React.ReactNode;
  id?: string;
};

export const DashboardCard = ({ area, title, subtitle, tooltip, action, toolbar, bodySx, children, id }: DashboardCardProps) => (
  <Card
    id={id}
    sx={{ gridArea: area, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', containerType: 'inline-size' } as SxProps<Theme>}
  >
    <Box sx={{ px: 2, pt: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {title}
          {tooltip ? (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          ) : null}
        </Typography>
        {subtitle ? <Typography variant="subtitle2" sx={{ mt: 0.25 }}>{subtitle}</Typography> : null}
      </Box>
      {action ? <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{action}</Box> : null}
    </Box>
    {toolbar ? <Box sx={{ px: 2, pt: 1.5 }}>{toolbar}</Box> : null}
    <Box sx={[{ p: 2, pt: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }, ...(Array.isArray(bodySx) ? bodySx : [bodySx])] as SxProps<Theme>}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Box>
  </Card>
);

/** Small coloured round icon used in Performance / KPI rows. */
export const DashboardIconBadge = ({ color, size = 44, children }: { color: string; size?: number; children?: React.ReactNode }) => (
  <Box
    sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color, bgcolor: alpha(color, 0.14),
      '& svg': { fontSize: size * 0.48 },
    }}
  >
    {children}
  </Box>
);
